import React, { useState } from "react";
import { toast } from "sonner";
import { Copy, ArrowSquareOut, Warning, ArrowsClockwise } from "@phosphor-icons/react";
import { appBaseUrl } from "../lib/appUrls";

/**
 * Customer Portal — User Access · Activation Link Modal
 * ------------------------------------------------------
 * Shown ONCE right after issuing (or regenerating) an activation link.
 * The raw token is only ever returned from the backend at issue time,
 * so this is the single surface that reveals it. Never store the token
 * elsewhere; if the operator misses this modal, the "Regenerate" button
 * mints a fresh one.
 *
 * Because AUTO_EMAIL_ENABLED=false in this environment, there is
 * intentionally no "Send email" affordance — Copy / Open are the only
 * two surfaces.
 */
export default function ActivationLinkModal({ open, onClose, token, email, onRegenerate, linkKind = "activation" }) {
  const [copied, setCopied] = useState(false);
  const [regenBusy, setRegenBusy] = useState(false);
  if (!open) return null;
  const path = linkKind === "reset"
    ? `/customer/reset-password?token=${encodeURIComponent(token || "")}`
    : `/portal/activate?token=${encodeURIComponent(token || "")}`;
  const url = `${appBaseUrl()}${path}`;
  const heading = linkKind === "reset" ? "Reset link ready" : "Activation link ready";
  const helpText = linkKind === "reset"
    ? "This one-time reset link expires in 45 minutes. Give it privately to the user."
    : "The invited user must sign in with Google using this email, OR create a password from the activation page.";
  const buttonText = linkKind === "reset" ? "Copy Reset Link" : "Copy Activation Link";
  const openText = linkKind === "reset" ? "Open Reset Link" : "Open Activation Link";
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Activation link copied");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Clipboard blocked — copy manually below");
    }
  };
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm"
      role="dialog"
      aria-modal
      data-testid="activation-link-modal"
    >
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-emerald-50 text-emerald-700 grid place-items-center">
            <ArrowSquareOut size={18} weight="bold" />
          </div>
          <div>
            <div className="text-base font-semibold text-slate-900">{heading}</div>
            <div className="text-xs text-slate-500">For {email}</div>
          </div>
        </div>
        <div className="px-5 py-4 space-y-3">
          <p className="text-sm text-slate-700">
            Send this link privately to <span className="font-mono text-xs">{email}</span>.
            {" "}{helpText}
          </p>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 flex items-start gap-2">
            <code className="flex-1 text-xs break-all text-slate-800" data-testid="activation-link-url">
              {url}
            </code>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={copy}
              data-testid="activation-copy-btn"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800"
            >
              <Copy size={16} weight="bold" />
              {copied ? "Copied" : buttonText}
            </button>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="activation-open-btn"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-100"
            >
              <ArrowSquareOut size={16} weight="bold" />
              {openText}
            </a>
            {onRegenerate && (
              <button
                onClick={async () => {
                  setRegenBusy(true);
                  try { await onRegenerate(); } finally { setRegenBusy(false); }
                }}
                disabled={regenBusy}
                data-testid="activation-regen-btn"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-indigo-300 text-sm text-indigo-700 hover:bg-indigo-50 disabled:opacity-60"
              >
                <ArrowsClockwise size={16} weight="bold" />
                {regenBusy ? "Regenerating…" : "Regenerate Link"}
              </button>
            )}
          </div>
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 flex gap-2">
            <Warning size={16} weight="bold" className="mt-[1px] shrink-0" />
            <span>
              This link is one-time-use and expires in{" "}
              {linkKind === "reset" ? "45 minutes" : "7 days"}. It is shown only once. If
              you close this dialog, click{" "}
              <strong>
                {linkKind === "reset" ? "Reset Link" : "Regenerate Activation Link"}
              </strong>{" "}
              on the user row to generate a fresh one.
            </span>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex justify-end">
          <button
            onClick={onClose}
            data-testid="activation-close-btn"
            className="px-3 py-1.5 rounded-lg bg-white border border-slate-300 text-sm text-slate-700 hover:bg-slate-100"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
